import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Switch } from '../ui/Switch';
import { Select } from '../ui/Select';
import { Slider } from '../ui/Slider';
import { Input } from '../ui/Input';
import { Stack } from '../layout/Stack';
import { 
  Bell, 
  Clock, 
  Volume2, 
  Vibrate, 
  Moon, 
  Calendar,
  Target,
  List,
  AlertCircle,
  Info,
  Settings
} from 'lucide-react';

interface NotificationSettingsProps {
  hasPermission: boolean;
}

interface NotificationConfig {
  // General settings
  enabled: boolean;
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
  };
  
  // Frequency controls
  maxPerHour: number;
  maxPerDay: number;
  batchSimilar: boolean;
  
  // Types
  scheduleReminders: boolean;
  deadlineWarnings: boolean;
  taskSuggestions: boolean;
  breakReminders: boolean;
  achievementCelebrations: boolean;
  
  // Timing preferences
  advanceNoticeMinutes: number;
  snoozeDefaultMinutes: number;
  
  // Smart features
  contextAware: boolean;
  learningEnabled: boolean;
  adaptiveTimingEnabled: boolean;
  
  // Presentation
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  showOnLockScreen: boolean;
  priorityFiltering: 'all' | 'medium_high' | 'high_critical';
  
  // Working hours integration
  respectWorkingHours: boolean;
  workingHours: {
    monday: { start: string; end: string; enabled: boolean };
    tuesday: { start: string; end: string; enabled: boolean };
    wednesday: { start: string; end: string; enabled: boolean };
    thursday: { start: string; end: string; enabled: boolean };
    friday: { start: string; end: string; enabled: boolean };
    saturday: { start: string; end: string; enabled: boolean };
    sunday: { start: string; end: string; enabled: boolean };
  };
}

const defaultConfig: NotificationConfig = {
  enabled: true,
  quietHours: {
    enabled: true,
    start: '22:00',
    end: '07:00'
  },
  maxPerHour: 100,
  maxPerDay: 1000,
  batchSimilar: true,
  scheduleReminders: true,
  deadlineWarnings: true,
  taskSuggestions: true,
  breakReminders: false,
  achievementCelebrations: true,
  advanceNoticeMinutes: 15,
  snoozeDefaultMinutes: 15,
  contextAware: true,
  learningEnabled: true,
  adaptiveTimingEnabled: true,
  soundEnabled: true,
  vibrationEnabled: true,
  showOnLockScreen: true,
  priorityFiltering: 'medium_high',
  respectWorkingHours: true,
  workingHours: {
    monday: { start: '09:00', end: '17:00', enabled: true },
    tuesday: { start: '09:00', end: '17:00', enabled: true },
    wednesday: { start: '09:00', end: '17:00', enabled: true },
    thursday: { start: '09:00', end: '17:00', enabled: true },
    friday: { start: '09:00', end: '17:00', enabled: true },
    saturday: { start: '10:00', end: '16:00', enabled: false },
    sunday: { start: '10:00', end: '16:00', enabled: false }
  }
};

export function NotificationSettings({ hasPermission }: NotificationSettingsProps) {
  const [config, setConfig] = useState<NotificationConfig>(defaultConfig);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem('godmode_notification_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        setConfig({ ...defaultConfig, ...parsed });
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  };

  const saveSettings = () => {
    try {
      localStorage.setItem('godmode_notification_settings', JSON.stringify(config));
      setHasUnsavedChanges(false);
      console.log('✅ Notification settings saved');
    } catch (error) {
      console.error('Failed to save notification settings:', error);
    }
  };

  const updateConfig = (updates: Partial<NotificationConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  const resetToDefaults = () => {
    setConfig(defaultConfig);
    setHasUnsavedChanges(true);
  };

  if (!hasPermission) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-orange-600">
            <AlertCircle className="h-5 w-5 mr-2" />
            Browser Permissions Required
          </CardTitle>
          <CardDescription>
            To configure notification settings, you need to first grant browser notification permissions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Please switch to the "Settings" tab and allow notifications when prompted.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Save Button */}
      {hasUnsavedChanges && (
        <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center">
            <Info className="h-4 w-4 mr-2 text-blue-600" />
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              You have unsaved changes
            </span>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Cancel
            </Button>
            <Button size="sm" onClick={saveSettings}>
              Save Changes
            </Button>
          </div>
        </div>
      )}

      {/* Master Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="h-5 w-5 mr-2" />
            Master Notification Control
          </CardTitle>
          <CardDescription>
            Global notification settings that affect all types
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Stack spacing="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Enable Notifications</p>
                <p className="text-xs text-muted-foreground">
                  Turn all notifications on or off
                </p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(enabled) => updateConfig({ enabled })}
              />
            </div>

            {config.enabled && (
              <>
                <div>
                  <p className="text-sm font-medium mb-2">Priority Filtering</p>
                  <Select
                    value={config.priorityFiltering}
                    onValueChange={(value) => 
                      updateConfig({ priorityFiltering: value as typeof config.priorityFiltering })
                    }
                  >
                    <option value="all">All notifications</option>
                    <option value="medium_high">Medium priority and above</option>
                    <option value="high_critical">High priority and critical only</option>
                  </Select>
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">
                    Maximum per hour: {config.maxPerHour}
                  </p>
                  <Slider
                    value={[config.maxPerHour]}
                    onValueChange={([value]) => updateConfig({ maxPerHour: value })}
                    max={100}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                </div>

                <div>
                  <p className="text-sm font-medium mb-2">
                    Maximum per day: {config.maxPerDay}
                  </p>
                  <Slider
                    value={[config.maxPerDay]}
                    onValueChange={([value]) => updateConfig({ maxPerDay: value })}
                    max={50}
                    min={5}
                    step={5}
                    className="w-full"
                  />
                </div>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Notification Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <List className="h-5 w-5 mr-2" />
            Notification Types
          </CardTitle>
          <CardDescription>
            Choose which types of notifications you want to receive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Stack spacing="md">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">Schedule Reminders</p>
                  <p className="text-xs text-muted-foreground">
                    Notifications for upcoming scheduled tasks
                  </p>
                </div>
              </div>
              <Switch
                checked={config.scheduleReminders}
                onCheckedChange={(scheduleReminders) => updateConfig({ scheduleReminders })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
                <div>
                  <p className="text-sm font-medium">Deadline Warnings</p>
                  <p className="text-xs text-muted-foreground">
                    Alerts for approaching deadlines
                  </p>
                </div>
              </div>
              <Switch
                checked={config.deadlineWarnings}
                onCheckedChange={(deadlineWarnings) => updateConfig({ deadlineWarnings })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Target className="h-4 w-4 mr-2 text-green-500" />
                <div>
                  <p className="text-sm font-medium">Task Suggestions</p>
                  <p className="text-xs text-muted-foreground">
                    AI-powered task recommendations
                  </p>
                </div>
              </div>
              <Switch
                checked={config.taskSuggestions}
                onCheckedChange={(taskSuggestions) => updateConfig({ taskSuggestions })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2 text-purple-500" />
                <div>
                  <p className="text-sm font-medium">Break Reminders</p>
                  <p className="text-xs text-muted-foreground">
                    Gentle reminders to take breaks
                  </p>
                </div>
              </div>
              <Switch
                checked={config.breakReminders}
                onCheckedChange={(breakReminders) => updateConfig({ breakReminders })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Target className="h-4 w-4 mr-2 text-yellow-500" />
                <div>
                  <p className="text-sm font-medium">Achievement Celebrations</p>
                  <p className="text-xs text-muted-foreground">
                    Celebrate when you complete goals
                  </p>
                </div>
              </div>
              <Switch
                checked={config.achievementCelebrations}
                onCheckedChange={(achievementCelebrations) => updateConfig({ achievementCelebrations })}
                disabled={!config.enabled}
              />
            </div>
          </Stack>
        </CardContent>
      </Card>

      {/* Timing Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Timing Preferences
          </CardTitle>
          <CardDescription>
            Control when and how often you receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Stack spacing="md">
            <div>
              <p className="text-sm font-medium mb-2">Default advance notice</p>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={config.advanceNoticeMinutes}
                  onChange={(e) => updateConfig({ advanceNoticeMinutes: parseInt(e.target.value) || 15 })}
                  min={5}
                  max={120}
                  className="w-20"
                  disabled={!config.enabled}
                />
                <span className="text-sm text-muted-foreground">minutes before</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Default snooze duration</p>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  value={config.snoozeDefaultMinutes}
                  onChange={(e) => updateConfig({ snoozeDefaultMinutes: parseInt(e.target.value) || 15 })}
                  min={5}
                  max={60}
                  className="w-20"
                  disabled={!config.enabled}
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Batch similar notifications</p>
                <p className="text-xs text-muted-foreground">
                  Group related notifications together
                </p>
              </div>
              <Switch
                checked={config.batchSimilar}
                onCheckedChange={(batchSimilar) => updateConfig({ batchSimilar })}
                disabled={!config.enabled}
              />
            </div>
          </Stack>
        </CardContent>
      </Card>

      {/* Smart Features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="h-5 w-5 mr-2" />
            Smart Features
            <Badge variant="secondary" className="ml-2">AI</Badge>
          </CardTitle>
          <CardDescription>
            Intelligent notification optimization powered by machine learning
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Stack spacing="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Context-aware timing</p>
                <p className="text-xs text-muted-foreground">
                  Optimize notification timing based on your activity
                </p>
              </div>
              <Switch
                checked={config.contextAware}
                onCheckedChange={(contextAware) => updateConfig({ contextAware })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Learning enabled</p>
                <p className="text-xs text-muted-foreground">
                  Learn from your responses to improve future notifications
                </p>
              </div>
              <Switch
                checked={config.learningEnabled}
                onCheckedChange={(learningEnabled) => updateConfig({ learningEnabled })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Adaptive timing</p>
                <p className="text-xs text-muted-foreground">
                  Automatically adjust notification timing for optimal effectiveness
                </p>
              </div>
              <Switch
                checked={config.adaptiveTimingEnabled}
                onCheckedChange={(adaptiveTimingEnabled) => updateConfig({ adaptiveTimingEnabled })}
                disabled={!config.enabled}
              />
            </div>

          </Stack>
        </CardContent>
      </Card>

      {/* Quiet Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Moon className="h-5 w-5 mr-2" />
            Quiet Hours
          </CardTitle>
          <CardDescription>
            Set times when you don't want to receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Stack spacing="md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Enable quiet hours</p>
                <p className="text-xs text-muted-foreground">
                  Pause notifications during specified times
                </p>
              </div>
              <Switch
                checked={config.quietHours.enabled}
                onCheckedChange={(enabled) => 
                  updateConfig({ quietHours: { ...config.quietHours, enabled } })
                }
                disabled={!config.enabled}
              />
            </div>

            {config.quietHours.enabled && config.enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium mb-2">Start time</p>
                  <Input
                    type="time"
                    value={config.quietHours.start}
                    onChange={(e) => 
                      updateConfig({ quietHours: { ...config.quietHours, start: e.target.value } })
                    }
                  />
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">End time</p>
                  <Input
                    type="time"
                    value={config.quietHours.end}
                    onChange={(e) => 
                      updateConfig({ quietHours: { ...config.quietHours, end: e.target.value } })
                    }
                  />
                </div>
              </div>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Presentation Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Volume2 className="h-5 w-5 mr-2" />
            Presentation
          </CardTitle>
          <CardDescription>
            How notifications appear and sound
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Stack spacing="md">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Volume2 className="h-4 w-4 mr-2" />
                <div>
                  <p className="text-sm font-medium">Sound notifications</p>
                  <p className="text-xs text-muted-foreground">Play sound when notifications arrive</p>
                </div>
              </div>
              <Switch
                checked={config.soundEnabled}
                onCheckedChange={(soundEnabled) => updateConfig({ soundEnabled })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Vibrate className="h-4 w-4 mr-2" />
                <div>
                  <p className="text-sm font-medium">Vibration</p>
                  <p className="text-xs text-muted-foreground">Vibrate device for notifications</p>
                </div>
              </div>
              <Switch
                checked={config.vibrationEnabled}
                onCheckedChange={(vibrationEnabled) => updateConfig({ vibrationEnabled })}
                disabled={!config.enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Show on lock screen</p>
                <p className="text-xs text-muted-foreground">Display notifications when device is locked</p>
              </div>
              <Switch
                checked={config.showOnLockScreen}
                onCheckedChange={(showOnLockScreen) => updateConfig({ showOnLockScreen })}
                disabled={!config.enabled}
              />
            </div>
          </Stack>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={resetToDefaults}>
          Reset to Defaults
        </Button>
        
        <Button onClick={saveSettings} disabled={!hasUnsavedChanges}>
          {hasUnsavedChanges ? 'Save Changes' : 'Settings Saved'}
        </Button>
      </div>
    </div>
  );
}